// Converts Konva canvas objects to a layered SVG for StickerIT print export.
// Output SVG has named groups: Artwork, SPOT_WHITE, Die Cut, Score Cut.
// Colors are output as rgb() so the backend can convert to CMYK.
//
// IMPORTANT: All coordinates are pre-computed to absolute world positions.
// We do NOT use scale() in SVG transforms because svg-to-pdfkit does not
// reliably handle scale() inside group transforms. Instead, scaleX/scaleY
// are multiplied directly into dimensions and positions.

export interface CanvasObjectForSvg {
  id: string;
  type: 'text' | 'rectangle' | 'circle' | 'qr-code' | 'image' | 'line' | 'polygon' | 'group' | 'arrow';
  x: number;
  y: number;
  width?: number;
  height?: number;
  radius?: number;
  text?: string;
  fill: string;
  stroke?: string;
  strokeWidth?: number;
  fontSize?: number;
  fontFamily?: string;
  imageData?: string;
  scaleX?: number;
  scaleY?: number;
  rotation?: number;
  points?: number[];
  cornerRadius?: number;
  qrModules?: boolean[][];
  layerType?: 'artwork' | 'spot_white' | 'die_cut' | 'kiss_cut';
  children?: CanvasObjectForSvg[];
  fontStyle?: string;
  textDecoration?: string;
  textAlign?: 'left' | 'center' | 'right';
  textBackground?: string;
  textBorderColor?: string;
  textBorderWidth?: number;
  pointerLength?: number;
  fillPriority?: string;
  fillLinearGradientStartPoint?: { x: number; y: number };
  fillLinearGradientEndPoint?: { x: number; y: number };
  fillLinearGradientColorStops?: (number | string)[];
}

function hexToRgb(color: string): string {
  if (!color || color === 'transparent' || color === 'none') return 'none';
  if (color.startsWith('rgb') || color.startsWith('hsl')) return color;
  if (!color.startsWith('#')) return color;
  const h = color.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return color;
  return `rgb(${r}, ${g}, ${b})`;
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function n(v: number): string {
  return parseFloat(v.toFixed(2)).toString();
}

function renderGradientDef(obj: CanvasObjectForSvg, id: string): string {
  const sp = obj.fillLinearGradientStartPoint!;
  const ep = obj.fillLinearGradientEndPoint!;
  const stops = obj.fillLinearGradientColorStops!;
  const sx = obj.scaleX ?? 1;
  const sy = obj.scaleY ?? 1;
  const stopElements: string[] = [];
  for (let i = 0; i < stops.length - 1; i += 2) {
    const offset = Number(stops[i]);
    const color = hexToRgb(String(stops[i + 1]));
    stopElements.push(`<stop offset="${offset}" stop-color="${color}" />`);
  }
  // Gradient coordinates are in local space; translate to world space
  return `<linearGradient id="${id}"
    x1="${n(obj.x + sp.x * sx)}" y1="${n(obj.y + sp.y * sy)}"
    x2="${n(obj.x + ep.x * sx)}" y2="${n(obj.y + ep.y * sy)}"
    gradientUnits="userSpaceOnUse">
    ${stopElements.join('\n    ')}
  </linearGradient>`;
}

export function canvasToSvg(
  objects: CanvasObjectForSvg[],
  backgroundColor: string,
  totalWidthPx: number,
  totalHeightPx: number
): string {
  const defs: string[] = [];
  let gradCounter = 0;

  function getFill(obj: CanvasObjectForSvg): string {
    if (
      obj.fillPriority === 'linear-gradient' &&
      obj.fillLinearGradientColorStops &&
      obj.fillLinearGradientStartPoint &&
      obj.fillLinearGradientEndPoint
    ) {
      const id = `grad-${gradCounter++}`;
      defs.push(renderGradientDef(obj, id));
      return `url(#${id})`;
    }
    return hexToRgb(obj.fill);
  }

  function rotateAttr(obj: CanvasObjectForSvg, cx: number, cy: number): string {
    return obj.rotation ? ` transform="rotate(${obj.rotation}, ${n(cx)}, ${n(cy)})"` : '';
  }

  function renderObject(obj: CanvasObjectForSvg): string {
    const sx = obj.scaleX ?? 1;
    const sy = obj.scaleY ?? 1;
    const fill = getFill(obj);
    const stroke = obj.stroke ? hexToRgb(obj.stroke) : 'none';
    const sw = obj.strokeWidth ?? 0;

    switch (obj.type) {
      case 'rectangle': {
        const w = (obj.width ?? 0) * sx;
        const h = (obj.height ?? 0) * sy;
        // Scale corner radius by the average scale to keep it proportional
        const cr = (obj.cornerRadius ?? 0) * Math.min(sx, sy);
        const cx = obj.x + w / 2;
        const cy = obj.y + h / 2;
        return `<rect x="${n(obj.x)}" y="${n(obj.y)}" width="${n(w)}" height="${n(h)}" rx="${n(cr)}" ry="${n(cr)}" fill="${fill}" stroke="${stroke}" stroke-width="${n(sw * Math.min(sx, sy))}"${rotateAttr(obj, cx, cy)} />`;
      }

      case 'circle': {
        const r = obj.radius ?? 0;
        // In Konva, circle x/y is the center
        const rx = r * sx;
        const ry = r * sy;
        const cx = obj.x;
        const cy = obj.y;
        return `<ellipse cx="${n(cx)}" cy="${n(cy)}" rx="${n(rx)}" ry="${n(ry)}" fill="${fill}" stroke="${stroke}" stroke-width="${n(sw)}"${rotateAttr(obj, cx, cy)} />`;
      }

      case 'text': {
        // Normalize: bake scaleY into fontSize so the baseline offset is not amplified.
        const fontSize = Math.round((obj.fontSize ?? 16) * sy);
        const fontFamily = obj.fontFamily ?? 'Arial';
        const fs = obj.fontStyle ?? '';
        const italic = fs.includes('italic') ? 'italic' : 'normal';
        const bold = fs.includes('bold') ? 'bold' : 'normal';
        const decoration = obj.textDecoration ?? 'none';
        const textWidth = (obj.width ?? 200) * sx;
        const align = obj.textAlign ?? 'left';
        const lineHeight = fontSize * 1.2;

        // Determine anchor x based on text alignment
        let anchorX: number;
        let textAnchor: string;
        if (align === 'center') { anchorX = obj.x + textWidth / 2; textAnchor = 'middle'; }
        else if (align === 'right') { anchorX = obj.x + textWidth; textAnchor = 'end'; }
        else { anchorX = obj.x; textAnchor = 'start'; }

        // Split on \n (normalize \r\n to \n first)
        const rawLines = (obj.text ?? '').replace(/\r\n/g, '\n').split('\n');

        // Background rect (if any)
        const hasBg = obj.textBackground && obj.textBackground !== 'transparent';
        const hasBorder = obj.textBorderColor && obj.textBorderColor !== 'transparent';
        const totalHeight = lineHeight * rawLines.length + fontSize * 0.4;
        const bgRect = (hasBg || hasBorder)
          ? `<rect x="${n(obj.x)}" y="${n(obj.y)}" width="${n(textWidth)}" height="${n(totalHeight)}" fill="${hasBg ? hexToRgb(obj.textBackground!) : 'none'}" stroke="${hasBorder ? hexToRgb(obj.textBorderColor!) : 'none'}" stroke-width="${n(obj.textBorderWidth ?? 1)}" />`
          : '';

        // Render each line as a separate <text> element so svg-to-pdfkit
        // doesn't have to support tspan dy (which it handles unreliably).
        const attrs = `font-size="${n(fontSize)}" font-family="${fontFamily}" font-style="${italic}" font-weight="${bold}" text-decoration="${decoration}" text-anchor="${textAnchor}" fill="${fill}" stroke="${stroke}" stroke-width="${n(sw)}"`;
        const textElements = rawLines
          .map((line, i) => {
            const y = obj.y + fontSize * 0.8 + i * lineHeight;
            return `<text x="${n(anchorX)}" y="${n(y)}" ${attrs}>${escapeXml(line)}</text>`;
          })
          .join('\n');

        return `${bgRect}${textElements}`;
      }

      case 'image': {
        if (!obj.imageData) return '';
        const w = (obj.width ?? 0) * sx;
        const h = (obj.height ?? 0) * sy;
        const cx = obj.x + w / 2;
        const cy = obj.y + h / 2;
        return `<image x="${n(obj.x)}" y="${n(obj.y)}" width="${n(w)}" height="${n(h)}" href="${obj.imageData}" preserveAspectRatio="none"${rotateAttr(obj, cx, cy)} />`;
      }

      case 'qr-code': {
        if (!obj.qrModules) return '';
        const size = obj.width ?? obj.height ?? 100;
        const modules = obj.qrModules;
        // Konva VectorQRCode uses effectiveWidth = width * scaleX for BOTH dimensions
        // (modules are always square). Mirror that: use sx for both axes.
        const ms = (size * sx) / modules.length;
        // Pre-compute absolute world coordinates for every module rect
        const rects = modules
          .flatMap((row, rowI) =>
            row.map((on, colI) => {
              if (!on) return '';
              const rx = obj.x + colI * ms;
              const ry = obj.y + rowI * ms;
              const rw = ms;
              const rh = ms;
              return `<rect x="${n(rx)}" y="${n(ry)}" width="${n(rw)}" height="${n(rh)}" />`;
            })
          )
          .filter(Boolean)
          .join('');
        return `<g fill="${fill}">${rects}</g>`;
      }

      case 'line': {
        if (!obj.points || obj.points.length < 4) return '';
        const pts: string[] = [];
        for (let i = 0; i < obj.points.length; i += 2) {
          pts.push(`${n(obj.x + obj.points[i] * sx)},${n(obj.y + obj.points[i + 1] * sy)}`);
        }
        return `<polyline points="${pts.join(' ')}" fill="none" stroke="${stroke !== 'none' ? stroke : fill}" stroke-width="${n(sw || 1)}" />`;
      }

      case 'polygon': {
        if (!obj.points || obj.points.length < 4) return '';
        const pts: string[] = [];
        for (let i = 0; i < obj.points.length; i += 2) {
          pts.push(`${n(obj.x + obj.points[i] * sx)},${n(obj.y + obj.points[i + 1] * sy)}`);
        }
        return `<polygon points="${pts.join(' ')}" fill="${fill}" stroke="${stroke}" stroke-width="${n(sw)}" />`;
      }

      case 'arrow': {
        if (!obj.points || obj.points.length < 4) return '';
        const pts = obj.points;
        const x1 = obj.x + pts[0] * sx;
        const y1 = obj.y + pts[1] * sy;
        const x2 = obj.x + pts[pts.length - 2] * sx;
        const y2 = obj.y + pts[pts.length - 1] * sy;
        const lineColor = stroke !== 'none' ? stroke : fill;
        const sw2 = sw || 2;
        const pl = (obj.pointerLength ?? 10) * Math.min(sx, sy);
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const ax1 = x2 - pl * Math.cos(angle - Math.PI / 6);
        const ay1 = y2 - pl * Math.sin(angle - Math.PI / 6);
        const ax2 = x2 - pl * Math.cos(angle + Math.PI / 6);
        const ay2 = y2 - pl * Math.sin(angle + Math.PI / 6);
        return `<line x1="${n(x1)}" y1="${n(y1)}" x2="${n(x2)}" y2="${n(y2)}" stroke="${lineColor}" stroke-width="${n(sw2)}" /><polygon points="${n(x2)},${n(y2)} ${n(ax1)},${n(ay1)} ${n(ax2)},${n(ay2)}" fill="${lineColor}" />`;
      }

      case 'group': {
        return (obj.children ?? []).map(renderObject).join('\n');
      }

      default:
        return '';
    }
  }

  // Group objects by layer type
  const layers: Record<'artwork' | 'spot_white' | 'die_cut' | 'kiss_cut', CanvasObjectForSvg[]> = {
    artwork: [],
    spot_white: [],
    die_cut: [],
    kiss_cut: [],
  };
  for (const obj of objects) {
    layers[obj.layerType ?? 'artwork'].push(obj);
  }

  const artworkSvg = layers.artwork.map(renderObject).join('\n');
  const spotWhiteSvg = layers.spot_white.map(renderObject).join('\n');
  const dieCutSvg = layers.die_cut.map(renderObject).join('\n');
  const kissCutSvg = layers.kiss_cut.map(renderObject).join('\n');

  const defsBlock = defs.length > 0 ? `<defs>\n  ${defs.join('\n  ')}\n</defs>\n  ` : '';

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
  width="${totalWidthPx}" height="${totalHeightPx}"
  viewBox="0 0 ${totalWidthPx} ${totalHeightPx}">
  ${defsBlock}<rect x="0" y="0" width="${totalWidthPx}" height="${totalHeightPx}" fill="${hexToRgb(backgroundColor)}" />
  <g id="Artwork">
    ${artworkSvg}
  </g>
  ${spotWhiteSvg ? `<g id="SPOT_WHITE">\n    ${spotWhiteSvg}\n  </g>` : ''}
  ${dieCutSvg ? `<g id="Die Cut">\n    ${dieCutSvg}\n  </g>` : ''}
  ${kissCutSvg ? `<g id="Score Cut">\n    ${kissCutSvg}\n  </g>` : ''}
</svg>`;
}
